import React, { useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import profile from "../assets/images/profile.png";
import pdfFile from "../assets/pdf/Hiren_cv.pdf";
import anime from "animejs";

const AboutUs = () => {
  const heroHeadingRef = useRef(null);
  const profileRef = useRef(null);
  const socialRef = useRef(null);
  const breadcrumbRef = useRef(null);
  const checklistRef = useRef(null);
  const aboutSectionRef = useRef(null);
  const personalInfoRef = useRef(null);
  const resumeSectionRef = useRef(null);
  const educationRef = useRef(null);
  const workExperienceRef = useRef(null);
  const followMeRef = useRef(null);
  const downloadBtnRef = useRef(null);

  useEffect(() => {
    // Intersection Observer options
    const observerOptions = {
      threshold: 0.1, // Trigger when 10% of the element is visible
      rootMargin: "0px 0px -50px 0px", // Trigger slightly before element fully enters viewport
    };

    // Create intersection observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const target = entry.target;

          // Trigger animations based on data attribute
          switch (target.dataset.animate) {
            case "breadcrumb":
              anime({
                targets: target,
                translateY: [-30, 0],
                opacity: [0, 1],
                easing: "easeOutExpo",
                duration: 800,
              });
              break;

            case "hero":
              anime({
                targets: target,
                translateY: [-50, 0],
                opacity: [0, 1],
                easing: "easeOutExpo",
                duration: 1200,
              });
              break;

            case "checklist":
              if (target.children) {
                anime({
                  targets: target.children,
                  translateX: [-50, 0],
                  opacity: [0, 1],
                  easing: "easeOutExpo",
                  duration: 800,
                  delay: anime.stagger(150),
                });
              }
              break;

            case "profile":
              anime({
                targets: target,
                scale: [0.7, 1],
                opacity: [0, 1],
                easing: "easeOutElastic(1, .8)",
                duration: 1500,
              });
              break;

            case "about-section":
              anime({
                targets: target,
                translateY: [40, 0],
                opacity: [0, 1],
                easing: "easeOutExpo",
                duration: 1000,
              });
              break;

            case "personal-info":
              if (target.children) {
                anime({
                  targets: target.children,
                  translateX: [40, 0],
                  opacity: [0, 1],
                  easing: "easeOutExpo",
                  duration: 800,
                  delay: anime.stagger(120),
                });
              }
              break;

            case "resume-section":
              anime({
                targets: target,
                translateY: [40, 0],
                opacity: [0, 1],
                easing: "easeOutExpo",
                duration: 1000,
              });
              break;

            case "download-btn":
              anime({
                targets: target,
                scale: [0.8, 1],
                opacity: [0, 1],
                easing: "easeOutBounce",
                duration: 1000,
              });
              break;

            case "education":
              if (target.children) {
                anime({
                  targets: target.children,
                  translateX: [50, 0],
                  opacity: [0, 1],
                  easing: "easeOutExpo",
                  duration: 800,
                  delay: anime.stagger(150),
                });
              }
              break;

            case "work-experience":
              if (target.children) {
                anime({
                  targets: target.children,
                  translateX: [50, 0],
                  opacity: [0, 1],
                  easing: "easeOutExpo",
                  duration: 800,
                  delay: anime.stagger(120),
                });
              }
              break;

            case "follow-me":
              anime({
                targets: target,
                translateY: [40, 0],
                opacity: [0, 1],
                easing: "easeOutExpo",
                duration: 1000,
              });
              break;

            case "social":
              if (target.children) {
                anime({
                  targets: target.children,
                  scale: [0.5, 1],
                  opacity: [0, 1],
                  rotateY: [180, 0],
                  delay: anime.stagger(200),
                  duration: 1000,
                  easing: "easeOutBack",
                });
              }
              break;
          }

          // Stop observing after animation triggers
          observer.unobserve(target);
        }
      });
    }, observerOptions);

    // Observe all elements with data-animate attribute
    const animatedElements = document.querySelectorAll("[data-animate]");
    animatedElements.forEach((el) => observer.observe(el));

    // Setup hover animations after a short delay
    setTimeout(() => {
      // Social icons hover animation
      if (socialRef.current && socialRef.current.children) {
        const socialIcons = socialRef.current.children;
        Array.from(socialIcons).forEach((icon) => {
          icon.addEventListener("mouseenter", () => {
            anime({
              targets: icon,
              scale: 1.2,
              rotateZ: 360,
              duration: 300,
              easing: "easeOutExpo",
            });
          });

          icon.addEventListener("mouseleave", () => {
            anime({
              targets: icon,
              scale: 1,
              rotateZ: 0,
              duration: 300,
              easing: "easeOutExpo",
            });
          });
        });
      }

      // Download button hover animation
      if (downloadBtnRef.current) {
        downloadBtnRef.current.addEventListener("mouseenter", () => {
          anime({
            targets: downloadBtnRef.current,
            scale: 1.05,
            duration: 200,
            easing: "easeOutQuad",
          });
        });

        downloadBtnRef.current.addEventListener("mouseleave", () => {
          anime({
            targets: downloadBtnRef.current,
            scale: 1,
            duration: 200,
            easing: "easeOutQuad",
          });
        });
      }
    }, 500);

    // Cleanup observer
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleDownload = (e) => {
    e.preventDefault();

    // Animation for download action
    if (downloadBtnRef.current) {
      anime({
        targets: downloadBtnRef.current,
        scale: [1, 0.95, 1],
        duration: 300,
        easing: "easeInOutQuad",
        complete: () => {
          const link = document.createElement("a");
          link.href = pdfFile;
          link.download = "Hiren_cv.pdf";
          link.click();
        },
      });
    }
  };

  return (
    <>
      <main className="page-wrapper">
        <Navbar />

        {/* Hero Section */}
        <section className="bg-dark position-relative py-5">
          <div
            className="d-none d-dark-mode-block position-absolute top-0 start-0 w-100 h-100"
            style={{ backgroundColor: "rgba(255,255,255, .03)" }}
          ></div>
          <div
            className="container position-relative z-2 py-5 mb-4 mb-sm-5"
            data-bs-theme="dark"
          >
            <div className="row pb-5 mb-2 mb-sm-0 mb-lg-3">
              <div className="col-lg-10 col-xl-9">
                <nav
                  aria-label="breadcrumb"
                  ref={breadcrumbRef}
                  data-animate="breadcrumb"
                  style={{ opacity: 0 }}
                >
                  <ol className="pt-lg-3 pb-lg-4 pb-2 breadcrumb">
                    <li className="breadcrumb-item">
                      <a href="/">Home</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      About Us
                    </li>
                  </ol>
                </nav>

                <h1
                  ref={heroHeadingRef}
                  data-animate="hero"
                  className="display-2 pb-2 pb-sm-3"
                  style={{ opacity: 0 }}
                >
                  Sharing Knowledge. Building Solutions
                </h1>

                <ul
                  ref={checklistRef}
                  data-animate="checklist"
                  className="list-unstyled d-flex flex-wrap mb-0"
                >
                  <li
                    className="d-flex text-body py-1 me-4"
                    style={{ opacity: 0 }}
                  >
                    <i className="ai-check-alt text-primary lead me-2"></i>
                    Web development insights
                  </li>
                  <li
                    className="d-flex text-body py-1 me-4"
                    style={{ opacity: 0 }}
                  >
                    <i className="ai-check-alt text-primary lead me-2"></i>
                    Real-world coding tutorials
                  </li>
                  <li className="d-flex text-body py-1" style={{ opacity: 0 }}>
                    <i className="ai-check-alt text-primary lead me-2"></i>
                    Developer journey & updates
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* About Section */}
        <section
          className="container position-relative z-3"
          style={{ marginTop: "-135px" }}
        >
          <div className="text-center">
            <img
              ref={profileRef}
              data-animate="profile"
              src={profile}
              className="img-fluid rounded-circle shadow"
              alt="profile"
              style={{
                maxWidth: "15rem",
                border: "5px solid #fff",
                opacity: 0,
              }}
            />
          </div>

          <div className="row pt-5 mt-n2 mt-sm-0 mt-md-2 mt-lg-6">
            <div className="col-md-6 col-lg-5">
              <div
                ref={aboutSectionRef}
                data-animate="about-section"
                style={{ opacity: 0 }}
              >
                <div className="fs-sm text-uppercase text-muted mb-3">
                  About Me
                </div>
                <h2 className="display-6">
                  I am a Software Engineer based in India
                </h2>
                <p className="fs-md text-muted">
                  Passionate about building software solutions that are
                  scalable, performant, and user-focused.
                </p>
              </div>
            </div>
            <div className="col-md-6 col-lg-6 pt-3 pt-md-5">
              <div ref={personalInfoRef} data-animate="personal-info">
                <p className="fs-lg" style={{ opacity: 0 }}>
                  <strong>Name:</strong> Hiren M. Hadiya
                </p>
                <p className="fs-lg" style={{ opacity: 0 }}>
                  <strong>Date of Birth:</strong> 05 - 09 - 2002
                </p>
                <p className="fs-lg" style={{ opacity: 0 }}>
                  <strong>Address:</strong> Surat, India
                </p>
                <p className="fs-lg" style={{ opacity: 0 }}>
                  <strong>Email:</strong> hiren@hadiya.in
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Resume Section */}
        <section className="container position-relative z-3">
          <div className="row pt-5 mt-sm-0 mt-md-2 mt-lg-4 mt-xl-5">
            <div className="col-md-5 col-lg-5">
              <div
                ref={resumeSectionRef}
                data-animate="resume-section"
                style={{ opacity: 0 }}
              >
                <div className="fs-sm text-uppercase text-muted mb-3">
                  Resume
                </div>
                <h2 className="display-6">Education & Work Experience</h2>
              </div>
              <div>
                <button
                  ref={downloadBtnRef}
                  data-animate="download-btn"
                  className="btn btn-primary mt-4"
                  onClick={handleDownload}
                  style={{ opacity: 0 }}
                >
                  Download CV
                </button>
              </div>
            </div>

            <div className="col-md-7 col-lg-7 pt-3 pt-md-5">
              <div ref={educationRef} data-animate="education">
                <h5 className="pt-4" style={{ opacity: 0 }}>
                  🎓 Education
                </h5>
                <p className="fs-lg mb-1" style={{ opacity: 0 }}>
                  <strong>Master of Computer Applications:</strong> Bhagwan
                  Mahavir University (Ongoing)
                </p>
                <p className="fs-lg " style={{ opacity: 0 }}>
                  <strong>Bachelor of Commerce:</strong> Ambaba Commerce College
                  (VNSGU)
                </p>
              </div>

              <div ref={workExperienceRef} data-animate="work-experience">
                <h5 className="pt-4" style={{ opacity: 0 }}>
                  💼 Work Experience
                </h5>
                <p className="fs-lg mb-1" style={{ opacity: 0 }}>
                  <strong>Software Developer:</strong> Techvivanta (Apr 2025 -
                  Present)
                </p>
                <p className="fs-lg mb-1" style={{ opacity: 0 }}>
                  <strong>Software Developer:</strong> Jabar Tech (Sep 2024 -
                  Mar 2025)
                </p>
                <p className="fs-lg mb-1" style={{ opacity: 0 }}>
                  <strong>Software Intern:</strong> Jabar Tech (May 2024 - Aug
                  2024)
                </p>
                <p className="fs-lg " style={{ opacity: 0 }}>
                  <strong>Software Training:</strong> Jabar Tech (Feb 2024 - Apr
                  2024)
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Follow Me Section */}
        <section className="container text-center py-5">
          <h3
            ref={followMeRef}
            data-animate="follow-me"
            className="display-6 mb-4"
            style={{ opacity: 0 }}
          >
            Follow Me
          </h3>
          <div
            ref={socialRef}
            data-animate="social"
            className="d-flex justify-content-center gap-4"
          >
            <a
              href="https://github.com/Lord-hiren"
              target="_blank"
              rel="noopener noreferrer"
              className="text-dark fs-3"
              style={{ opacity: 0 }}
            >
              <i className="ai-github"></i>
            </a>
            <a
              href="https://www.instagram.com/hiren_hadiya_509/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-danger fs-3"
              style={{ opacity: 0 }}
            >
              <i className="ai-instagram"></i>
            </a>
            <a
              href="https://www.linkedin.com/in/hiren-hadiya-6a483a2a2/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary fs-3"
              style={{ opacity: 0 }}
            >
              <i className="ai-linkedin"></i>
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default AboutUs;
